import { Download } from "lucide-react";
import type { RaRecord, UseBulkExportOptions } from "ra-core";
import {
  useBulkExport,
  useCanAccess,
  useDataProvider,
  useGetResourceLabel,
  useListContext,
  useResourceContext,
  useResourceTranslation,
} from "ra-core";

import { Button } from "../ui/button";

export const BulkExportButton = <T extends RaRecord>({
  icon = defaultIcon,
  label: labelProp,
  onClick,
  ...props
}: BulkExportButtonProps<T>) => {
  const bulkExport = useBulkExport(props);
  const resource = useResourceContext(props);
  const resourceName = resource ?? "";
  const { selectedIds = [] } = useListContext();
  const dataProvider = useDataProvider();
  const access = useCanAccess({ resource: resourceName, action: "export" });
  const getResourceLabel = useGetResourceLabel();
  const label = useResourceTranslation({
    resourceI18nKey: resource
      ? `resources.${resource}.action.export`
      : undefined,
    baseI18nKey: "ra.action.export",
    options: { name: resource ? getResourceLabel(resource, 1) : undefined },
    userText: labelProp,
  });

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const auditProvider = dataProvider as typeof dataProvider & {
      recordExport?: (resource: string, count: number) => Promise<void>;
    };
    await auditProvider.recordExport?.(resourceName, selectedIds.length);
    await bulkExport();
    onClick?.(event);
  };

  if (!resource || access.isPending || !access.canAccess) return null;

  return (
    <Button
      onClick={handleClick}
      role="button"
      variant="outline"
      size="sm"
      className="flex h-9 items-center gap-2"
      aria-label={typeof label === "string" ? label : undefined}
      {...sanitizeRestProps(props)}
    >
      {icon}
      {label}
    </Button>
  );
};

const defaultIcon = <Download className="h-4 w-4" />;

export type BulkExportButtonProps<T extends RaRecord> =
  UseBulkExportOptions<T> & {
    icon?: React.ReactNode;
    label?: string;
  } & React.ComponentProps<typeof Button>;

const sanitizeRestProps = <T extends RaRecord>({
  resource: _resource,
  exporter: _exporter,
  onClick: _onClick,
  label: _label,
  icon: _icon,
  meta: _meta,
  ...rest
}: BulkExportButtonProps<T>) => rest;
